import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldCheck,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineExclamationTriangle,
  HiOutlineCheckBadge,
} from "react-icons/hi2";
import { authService } from "../../services";
import safetyObservationService from "../../services/safetyObservationService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./SafetyBehaviorCategories.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedSafetyCategoriesData = () => {
  try {
    const cachedCategories = sessionStorage.getItem(
      "safety_observation_categories_data",
    );
    return {
      categories: cachedCategories ? JSON.parse(cachedCategories) : [],
      hasCategories: !!cachedCategories,
    };
  } catch (e) {
    return { categories: [], hasCategories: false };
  }
};

const SafetyBehaviorCategories = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedSafetyCategoriesData();
  const [categories, setCategories] = useState(cachedData.categories);
  const [loading, setLoading] = useState(!cachedData.hasCategories);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [categoryFormData, setCategoryFormData] = useState({
    categoryCode: "",
    categoryName: "",
    description: "",
    isActive: true,
    displayOrder: 0,
    behaviorType: "",
  });
  const [subCategoryFormData, setSubCategoryFormData] = useState({
    subCategoryCode: "",
    subCategoryName: "",
    description: "",
    isActive: true,
    displayOrder: 0,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedSafetyCategoriesData();
      if (refreshedCachedData.hasCategories) {
        setCategories(refreshedCachedData.categories);
        // Do not expand categories by default
        setExpandedCategories(new Set());
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to view categories");
      setLoading(false);
      return;
    }

    // Check if we have cached data - if yes, show page immediately
    const hasCachedData = sessionStorage.getItem(
      "safety_observation_categories_data",
    );

    // If we have cached data, load fresh data in background
    if (hasCachedData) {
      setLoading(false);
      loadCategories();
    } else {
      // Only show loading if we don't have cached data
      loadCategories();
    }
  }, [tenantId]);

  const loadCategories = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem(
        "safety_observation_categories_data",
      );
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");
      const data = await safetyObservationService.getCategories();
      setCategories(data || []);
      // Do not expand categories by default
      setExpandedCategories(new Set());

      // Cache the data
      sessionStorage.setItem(
        "safety_observation_categories_data",
        JSON.stringify(data || []),
      );
    } catch (err) {
      setError("Failed to load categories. Please try again.");
      console.error("Error loading categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const [openGroup, setOpenGroup] = useState(null); // Default to none open

  const filteredCategories = categories.filter(
    (category) =>
      category.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.categoryCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.subCategories?.some(
        (sc) =>
          sc.subCategoryName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          sc.subCategoryCode?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  const safeList = filteredCategories.filter(
    (c) =>
      c.behaviorType === "Safe" ||
      (!c.behaviorType && !c.categoryName.toUpperCase().includes("UNSAFE")),
  );
  const unsafeList = filteredCategories.filter(
    (c) =>
      c.behaviorType === "Unsafe" ||
      (!c.behaviorType && c.categoryName.toUpperCase().includes("UNSAFE")),
  );

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      categoryCode: "",
      categoryName: "",
      description: "",
      isActive: true,
      displayOrder: categories.length,
      behaviorType: "",
    });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      categoryCode: category.categoryCode || "",
      categoryName: category.categoryName || "",
      description: category.description || "",
      isActive: category.isActive !== undefined ? category.isActive : true,
      displayOrder: category.displayOrder || 0,
      behaviorType:
        category.behaviorType ||
        (category.categoryName.toUpperCase().includes("UNSAFE")
          ? "Unsafe"
          : "Safe"),
    });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const handleAddSubCategory = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setEditingSubCategory(null);
    const category = categories.find((c) => c.categoryId === categoryId);
    const subCategories = category?.subCategories || [];
    setSubCategoryFormData({
      subCategoryCode: "",
      subCategoryName: "",
      description: "",
      isActive: true,
      displayOrder: subCategories.length,
    });
    setFormErrors({});
    setShowSubCategoryModal(true);
  };

  const handleEditSubCategory = (subCategory, categoryId) => {
    setSelectedCategoryId(categoryId);
    setEditingSubCategory(subCategory);
    setSubCategoryFormData({
      subCategoryCode: subCategory.subCategoryCode || "",
      subCategoryName: subCategory.subCategoryName || "",
      description: subCategory.description || "",
      isActive:
        subCategory.isActive !== undefined ? subCategory.isActive : true,
      displayOrder: subCategory.displayOrder || 0,
    });
    setFormErrors({});
    setShowSubCategoryModal(true);
  };

  const handleDeleteCategory = async (id) => {
    try {
      setError("");
      await safetyObservationService.deleteCategory(id);
      await loadCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete category. Please try again.");
      console.error("Error deleting category:", err);
    }
  };

  const handleDeleteSubCategory = async (id) => {
    try {
      setError("");
      await safetyObservationService.deleteSubCategory(id);
      await loadCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete subcategory. Please try again.");
      console.error("Error deleting subcategory:", err);
    }
  };

  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryFormData.categoryName.trim()) {
      errors.categoryName = "Category name is required";
    }
    if (!categoryFormData.behaviorType) {
      errors.behaviorType = "Behavior type is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSubCategoryForm = () => {
    const errors = {};
    if (!subCategoryFormData.subCategoryName.trim()) {
      errors.subCategoryName = "Subcategory name is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();

    if (!validateCategoryForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const categoryData = {
        tenantId: tenantId,
        categoryCode: categoryFormData.categoryCode.trim() || null,
        categoryName: categoryFormData.categoryName.trim(),
        description: categoryFormData.description.trim() || null,
        isActive: categoryFormData.isActive,
        displayOrder: categoryFormData.displayOrder || 0,
        behaviorType: categoryFormData.behaviorType,
        // Explicitly exclude subCategories to prevent validation errors
      };

      if (editingCategory) {
        categoryData.categoryId = editingCategory.categoryId;
        await safetyObservationService.updateCategory(
          editingCategory.categoryId,
          categoryData,
        );
      } else {
        await safetyObservationService.createCategory(categoryData);
      }

      await loadCategories();
      setShowCategoryModal(false);
      setCategoryFormData({
        categoryCode: "",
        categoryName: "",
        description: "",
        isActive: true,
        displayOrder: 0,
        behaviorType: "Unsafe",
      });
      setEditingCategory(null);
    } catch (err) {
      let errorMessage = "Failed to save category. Please try again.";

      if (err.response?.data) {
        // Check for message string
        if (typeof err.response.data.message === "string") {
          errorMessage = err.response.data.message;
        }
        // Check for errors object - extract first error message
        else if (err.response.data.errors) {
          const errors = err.response.data.errors;
          if (typeof errors === "string") {
            errorMessage = errors;
          } else if (typeof errors === "object") {
            // Extract first error message from object
            const firstErrorKey = Object.keys(errors)[0];
            if (firstErrorKey) {
              const firstError = errors[firstErrorKey];
              if (Array.isArray(firstError)) {
                errorMessage = firstError[0] || errorMessage;
              } else if (typeof firstError === "string") {
                errorMessage = firstError;
              } else {
                errorMessage = `Validation error: ${firstErrorKey}`;
              }
            }
          }
        }
        // Check for error string
        else if (typeof err.response.data.error === "string") {
          errorMessage = err.response.data.error;
        }
      } else if (err.message && typeof err.message === "string") {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubCategorySubmit = async (e) => {
    e.preventDefault();

    if (!validateSubCategoryForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const subCategoryData = {
        tenantId: tenantId,
        categoryId: selectedCategoryId,
        subCategoryCode: subCategoryFormData.subCategoryCode.trim() || null,
        subCategoryName: subCategoryFormData.subCategoryName.trim(),
        description: subCategoryFormData.description.trim() || null,
        isActive: subCategoryFormData.isActive,
        displayOrder: subCategoryFormData.displayOrder || 0,
      };

      if (editingSubCategory) {
        subCategoryData.subCategoryId = editingSubCategory.subCategoryId;
        await safetyObservationService.updateSubCategory(
          editingSubCategory.subCategoryId,
          subCategoryData,
        );
      } else {
        await safetyObservationService.createSubCategory(
          selectedCategoryId,
          subCategoryData,
        );
      }

      await loadCategories();
      setShowSubCategoryModal(false);
      setSubCategoryFormData({
        subCategoryCode: "",
        subCategoryName: "",
        description: "",
        isActive: true,
        displayOrder: 0,
      });
      setEditingSubCategory(null);
      setSelectedCategoryId(null);
    } catch (err) {
      let errorMessage = "Failed to save subcategory. Please try again.";

      if (err.response?.data) {
        // Check for message string
        if (typeof err.response.data.message === "string") {
          errorMessage = err.response.data.message;
        }
        // Check for errors object - extract first error message
        else if (err.response.data.errors) {
          const errors = err.response.data.errors;
          if (typeof errors === "string") {
            errorMessage = errors;
          } else if (typeof errors === "object") {
            // Extract first error message from object
            const firstErrorKey = Object.keys(errors)[0];
            if (firstErrorKey) {
              const firstError = errors[firstErrorKey];
              if (Array.isArray(firstError)) {
                errorMessage = firstError[0] || errorMessage;
              } else if (typeof firstError === "string") {
                errorMessage = firstError;
              } else {
                errorMessage = `Validation error: ${firstErrorKey}`;
              }
            }
          }
        }
        // Check for error string
        else if (typeof err.response.data.error === "string") {
          errorMessage = err.response.data.error;
        }
      } else if (err.message && typeof err.message === "string") {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoading message="Loading categories..." />;
  }

  return (
    <div className="pagecontainer safety-categories-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <HiOutlineShieldCheck className="page-icon" />
            Safety Behavior
          </h1>
          <p>Manage categorized safety observations</p>
        </div>

        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button className="btn-primary" onClick={handleAddCategory}>
            <HiOutlinePlus /> Add Category
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}



      <div className="categories-list grouped-view">
        {/* Unsafe Group */}
        <div
          className={`group-main-card unsafe ${openGroup === "UNSAFE" ? "active" : ""}`}
        >
          <div
            className="group-main-header"
            onClick={() =>
              setOpenGroup(openGroup === "UNSAFE" ? null : "UNSAFE")
            }
          >
            <div className="group-title">
              <span className="group-icon-wrapper unsafe">
                <HiOutlineExclamationTriangle />
              </span>
              <div>
                <h2>Unsafe Behaviors</h2>
                <p className="group-subtitle">Risky actions or conditions</p>
              </div>
              <span className="count-badge">{unsafeList.length}</span>
            </div>
            <button className="expand-group-btn">
              {openGroup === "UNSAFE" ? (
                <HiOutlineChevronDown />
              ) : (
                <HiOutlineChevronRight />
              )}
            </button>
          </div>

          {openGroup === "UNSAFE" && (
            <div className="group-content">
              {unsafeList.length === 0 ? (
                <div className="empty-subcategories">
                  <p>No unsafe categories found.</p>
                </div>
              ) : (
                unsafeList.map((category) => (
                  <div key={category.categoryId} className="category-card">
                    <div className="category-header">
                      <button
                        className="expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category.categoryId);
                        }}
                      >
                        {expandedCategories.has(category.categoryId) ? (
                          <HiOutlineChevronDown />
                        ) : (
                          <HiOutlineChevronRight />
                        )}
                      </button>
                      <div className="category-info">
                        <div className="category-title-row">
                          <h3>
                            {category.categoryName
                              .replace(
                                /—\s*(UNSAFE|SAFE)|-\s*(UNSAFE|SAFE)/gi,
                                "",
                              )
                              .trim()}
                          </h3>
                          {category.categoryCode && (
                            <span className="category-code">
                              {category.categoryCode}
                            </span>
                          )}
                        </div>

                        {category.description && (
                          <p className="category-description">
                            {category.description}
                          </p>
                        )}
                      </div>
                      <div className="category-actions">
                        <span
                          className={`status-badge ${category.isActive ? "active" : "inactive"}`}
                        >
                          {category.isActive ? "Active" : "Inactive"}
                        </span>
                        <button
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddSubCategory(category.categoryId);
                          }}
                          title="Add Subcategory"
                        >
                          <HiOutlinePlus />
                        </button>
                        <button
                          className="btn-icon-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category);
                          }}
                          title="Edit Category"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm({
                              type: "category",
                              id: category.categoryId,
                              name: category.categoryName,
                            });
                          }}
                          title="Delete Category"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </div>

                    {expandedCategories.has(category.categoryId) && (
                      <div className="subcategories-list">
                        {category.subCategories &&
                          category.subCategories.length > 0 ? (
                          category.subCategories.map((subCategory) => (
                            <div
                              key={subCategory.subCategoryId}
                              className="subcategory-item"
                            >
                              <div className="subcategory-info">
                                <h4>{subCategory.subCategoryName}</h4>
                                {subCategory.subCategoryCode && (
                                  <span className="subcategory-code">
                                    {subCategory.subCategoryCode}
                                  </span>
                                )}
                                {subCategory.description && (
                                  <p
                                    className="category-description"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {subCategory.description}
                                  </p>
                                )}
                              </div>
                              <div className="subcategory-actions">
                                <span
                                  className={`status-badge ${subCategory.isActive ? "active" : "inactive"}`}
                                >
                                  {subCategory.isActive ? "Active" : "Inactive"}
                                </span>
                                <button
                                  className="btn-icon-edit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSubCategory(
                                      subCategory,
                                      category.categoryId,
                                    );
                                  }}
                                  title="Edit Subcategory"
                                >
                                  <HiOutlinePencil size={14} />
                                </button>
                                <button
                                  className="btn-icon-delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm({
                                      type: "subcategory",
                                      id: subCategory.subCategoryId,
                                      name: subCategory.subCategoryName,
                                    });
                                  }}
                                  title="Delete Subcategory"
                                >
                                  <HiOutlineTrash size={14} />
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="empty-subcategories">
                            <p>No subcategories. Click + to add.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Safe Group */}
        <div
          className={`group-main-card safe ${openGroup === "SAFE" ? "active" : ""}`}
        >
          <div
            className="group-main-header"
            onClick={() => setOpenGroup(openGroup === "SAFE" ? null : "SAFE")}
          >
            <div className="group-title">
              <span className="group-icon-wrapper safe">
                <HiOutlineCheckBadge />
              </span>
              <div>
                <h2>Safe Behaviors</h2>
                <p className="group-subtitle">Positive actions to encourage</p>
              </div>
              <span className="count-badge">{safeList.length}</span>
            </div>
            <button className="expand-group-btn">
              {openGroup === "SAFE" ? (
                <HiOutlineChevronDown />
              ) : (
                <HiOutlineChevronRight />
              )}
            </button>
          </div>

          {openGroup === "SAFE" && (
            <div className="group-content">
              {safeList.length === 0 ? (
                <div className="empty-subcategories">
                  <p>No safe categories found.</p>
                </div>
              ) : (
                safeList.map((category) => (
                  <div key={category.categoryId} className="category-card">
                    <div className="category-header">
                      <button
                        className="expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category.categoryId);
                        }}
                      >
                        {expandedCategories.has(category.categoryId) ? (
                          <HiOutlineChevronDown />
                        ) : (
                          <HiOutlineChevronRight />
                        )}
                      </button>
                      <div className="category-info">
                        <div className="category-title-row">
                          <h3>
                            {category.categoryName
                              .replace(
                                /—\s*(UNSAFE|SAFE)|-\s*(UNSAFE|SAFE)/gi,
                                "",
                              )
                              .trim()}
                          </h3>
                          {category.categoryCode && (
                            <span className="category-code">
                              {category.categoryCode}
                            </span>
                          )}
                        </div>

                        {category.description && (
                          <p className="category-description">
                            {category.description}
                          </p>
                        )}
                      </div>
                      <div className="category-actions">
                        <span
                          className={`status-badge ${category.isActive ? "active" : "inactive"}`}
                        >
                          {category.isActive ? "Active" : "Inactive"}
                        </span>
                        <button
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddSubCategory(category.categoryId);
                          }}
                          title="Add Subcategory"
                        >
                          <HiOutlinePlus />
                        </button>
                        <button
                          className="btn-icon-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category);
                          }}
                          title="Edit Category"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm({
                              type: "category",
                              id: category.categoryId,
                              name: category.categoryName,
                            });
                          }}
                          title="Delete Category"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </div>

                    {expandedCategories.has(category.categoryId) && (
                      <div className="subcategories-list">
                        {category.subCategories &&
                          category.subCategories.length > 0 ? (
                          category.subCategories.map((subCategory) => (
                            <div
                              key={subCategory.subCategoryId}
                              className="subcategory-item"
                            >
                              <div className="subcategory-info">
                                <h4>{subCategory.subCategoryName}</h4>
                                {subCategory.subCategoryCode && (
                                  <span className="subcategory-code">
                                    {subCategory.subCategoryCode}
                                  </span>
                                )}
                                {subCategory.description && (
                                  <p
                                    className="category-description"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {subCategory.description}
                                  </p>
                                )}
                              </div>
                              <div className="subcategory-actions">
                                <span
                                  className={`status-badge ${subCategory.isActive ? "active" : "inactive"}`}
                                >
                                  {subCategory.isActive ? "Active" : "Inactive"}
                                </span>
                                <button
                                  className="btn-icon-edit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSubCategory(
                                      subCategory,
                                      category.categoryId,
                                    );
                                  }}
                                  title="Edit Subcategory"
                                >
                                  <HiOutlinePencil size={14} />
                                </button>
                                 <button
                                   className="btn-icon-delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm({
                                      type: "subcategory",
                                      id: subCategory.subCategoryId,
                                      name: subCategory.subCategoryName,
                                    });
                                  }}
                                  title="Delete Subcategory"
                                >
                                  <HiOutlineTrash size={14} />
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="empty-subcategories">
                            <p>No subcategories. Click + to add.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Category Modal */}
      <SharedModal
        isOpen={showCategoryModal}
        onClose={() => !submitting && setShowCategoryModal(false)}
        title={editingCategory ? "Edit Category" : "Add Category"}
        customWidth="650px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowCategoryModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="category-form"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Saving..." : editingCategory ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="category-form" onSubmit={handleCategorySubmit}>
            <div className="form-group">
              <label>Behavior Type <span className="required">*</span></label>
              <select
                value={categoryFormData.behaviorType}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    behaviorType: e.target.value,
                  })
                }
                required
              >
                <option value="">Select Behavior Type</option>
                <option value="Safe">Safe Behavior</option>
                <option value="Unsafe">Unsafe Behavior</option>
              </select>
              {formErrors.behaviorType && (
                <span className="field-error">{formErrors.behaviorType}</span>
              )}
            </div>
            <div className="form-group">
              <label>Category Code</label>
              <input
                type="text"
                value={categoryFormData.categoryCode}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    categoryCode: e.target.value,
                  })
                }
                placeholder="Enter category code"
              />
            </div>
            <div className="form-group">
              <label>
                Category Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={categoryFormData.categoryName}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    categoryName: e.target.value,
                  })
                }
                placeholder="Enter category name"
                required
              />
              {formErrors.categoryName && (
                <span className="field-error">{formErrors.categoryName}</span>
              )}
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={categoryFormData.description}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    description: e.target.value,
                  })
                }
                placeholder="Enter description"
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={categoryFormData.displayOrder}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <div className="status-container">
                  <label className="switch-styled">
                    <input
                      type="checkbox"
                      checked={categoryFormData.isActive}
                      onChange={(e) =>
                        setCategoryFormData({
                          ...categoryFormData,
                          isActive: e.target.checked,
                        })
                      }
                    />
                    <span className="slider-styled"></span>
                  </label>
                  <span className="status-text">
                    {categoryFormData.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* SubCategory Modal */}
      <SharedModal
        isOpen={showSubCategoryModal}
        onClose={() => !submitting && setShowSubCategoryModal(false)}
        title={editingSubCategory ? "Edit Subcategory" : "Add Subcategory"}
        customWidth="600px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSubCategoryModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sub-category-form"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Saving..." : editingSubCategory ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sub-category-form" onSubmit={handleSubCategorySubmit}>
            <div className="form-group">
              <label>Subcategory Code</label>
              <input
                type="text"
                value={subCategoryFormData.subCategoryCode}
                onChange={(e) =>
                  setSubCategoryFormData({
                    ...subCategoryFormData,
                    subCategoryCode: e.target.value,
                  })
                }
                placeholder="Enter subcategory code"
              />
            </div>
            <div className="form-group">
              <label>
                Subcategory Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={subCategoryFormData.subCategoryName}
                onChange={(e) =>
                  setSubCategoryFormData({
                    ...subCategoryFormData,
                    subCategoryName: e.target.value,
                  })
                }
                placeholder="Enter subcategory name"
                required
              />
              {formErrors.subCategoryName && (
                <span className="field-error">{formErrors.subCategoryName}</span>
              )}
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={subCategoryFormData.description}
                onChange={(e) =>
                  setSubCategoryFormData({
                    ...subCategoryFormData,
                    description: e.target.value,
                  })
                }
                placeholder="Enter description"
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={subCategoryFormData.displayOrder}
                  onChange={(e) =>
                    setSubCategoryFormData({
                      ...subCategoryFormData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <div className="status-container">
                  <label className="switch-styled">
                    <input
                      type="checkbox"
                      checked={subCategoryFormData.isActive}
                      onChange={(e) =>
                        setSubCategoryFormData({
                          ...subCategoryFormData,
                          isActive: e.target.checked,
                        })
                      }
                    />
                    <span className="slider-styled"></span>
                  </label>
                  <span className="status-text">
                    {subCategoryFormData.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (deleteConfirm.type === "category") {
                  handleDeleteCategory(deleteConfirm.id);
                } else {
                  handleDeleteSubCategory(deleteConfirm.id);
                }
              }}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            {deleteConfirm?.type === "category" ? "category" : "subcategory"}{" "}
            <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default SafetyBehaviorCategories;
